import { action, makeObservable, observable, runInAction } from "mobx"
import { ACTION_COLORS, ACTION_ICONS, type SavedAction, type SavedActionColor, type SavedActionIcon } from "../actions/actionTypes"
import { retryBusy } from "../nest/appDataRetry"
import {
    NestClientError,
    type NestMethodParams,
    type NestMethodResult,
    type NestNotificationParams,
    type NestSubscriptionFilter,
    nestClient,
    requestNestMethod,
} from "../nest/client"
import type {
    TaskTimelineActionEntry,
    TaskTimelineActionSnapshot,
    TaskTimelineAnchor,
    TaskTimelineChapterEntry,
    TaskTimelineDoEntry,
    TaskTimelineEntry,
    TaskTimelineRecord,
} from "../timeline/taskTimelineTypes"
import { ulid } from "../utils/ulid"

export const TASK_TIMELINE_NAMESPACE = "wildlark.timeline"
export const TASK_TIMELINE_KEY_PREFIX = "task."

const TASK_TIMELINE_KEY_SUFFIX = ".v1"
const MAX_TIMELINE_ENTRIES = 512
const MAX_TIMELINE_RECORD_BYTES = 240 * 1024
const MAX_ENTRY_ID_LENGTH = 240
const MAX_TASK_ID_LENGTH = 160
const MAX_SESSION_ID_LENGTH = 240
const MAX_TURN_ID_LENGTH = 240
const MAX_ACTION_ID_LENGTH = 160
const MAX_ACTION_NAME_LENGTH = 80
const MAX_CHAPTER_TITLE_LENGTH = 80
const MAX_COMMENT_COUNT = 10_000

type TaskTimelineMethod = "appData.get" | "appData.set" | "appData.delete"
export type TaskTimelineRequest = <M extends TaskTimelineMethod>(method: M, params: NestMethodParams<M>) => Promise<NestMethodResult<M>>

export interface TaskTimelineSubscriptionClient {
    retainSubscriptions(filters: NestSubscriptionFilter[]): Promise<() => void>
    on(method: "appData.changed", handler: (params: NestNotificationParams<"appData.changed"> | undefined) => void): () => void
}

interface TaskTimelineStoreOptions {
    request?: TaskTimelineRequest
    client?: TaskTimelineSubscriptionClient
    now?: () => string
}

interface AppendEntryParams {
    taskId: string
    workspaceId: string
    id?: string
    createdAt?: string
    anchor?: TaskTimelineAnchor
}

export interface AppendTaskTimelineChapterParams extends AppendEntryParams {
    title: string
    source: TaskTimelineChapterEntry["source"]
}

export interface AppendTaskTimelineActionParams extends AppendEntryParams {
    actions: readonly SavedAction[]
    withInstructions: boolean
    replacesEntryId?: string
}

export interface AppendTaskTimelineDoParams extends AppendEntryParams {
    commentCount: number
}

export interface RemoveTaskTimelineActionParams {
    taskId: string
    workspaceId: string
    turnId: string
}

export interface RemoveTaskTimelineDoParams {
    taskId: string
    workspaceId: string
    clientRequestId: string
}

export interface MoveTaskTimelineInvocationParams {
    taskId: string
    workspaceId: string
    fromEntryId: string
    toEntryId: string
    anchor: TaskTimelineAnchor
}

type TaskTimelineMutation =
    | { type: "append"; entry: TaskTimelineEntry; replacesEntryId?: string }
    | { type: "remove"; entryId: string }
    | { type: "move"; fromEntryId: string; toEntryId: string; anchor: TaskTimelineAnchor }

const defaultRequest: TaskTimelineRequest = (method, params) => requestNestMethod(method, params)
const ACTION_ICON_SET = new Set<string>(ACTION_ICONS)
const ACTION_COLOR_SET = new Set<string>(ACTION_COLORS)

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isNonEmptyString(value: unknown, maxLength: number): value is string {
    return typeof value === "string" && value.length > 0 && value.length <= maxLength
}

function isCommentCount(value: unknown): value is number {
    return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= MAX_COMMENT_COUNT
}

function isSavedActionIcon(value: unknown): value is SavedActionIcon {
    return typeof value === "string" && ACTION_ICON_SET.has(value)
}

function isSavedActionColor(value: unknown): value is SavedActionColor {
    return typeof value === "string" && ACTION_COLOR_SET.has(value)
}

function parseAnchor(value: unknown): TaskTimelineAnchor | null {
    if (!isRecord(value) || !isNonEmptyString(value.sessionId, MAX_SESSION_ID_LENGTH)) return null
    if (value.turnId !== undefined && !isNonEmptyString(value.turnId, MAX_TURN_ID_LENGTH)) return null
    return {
        sessionId: value.sessionId,
        ...(value.turnId ? { turnId: value.turnId } : {}),
    }
}

function parseActionSnapshot(value: unknown): TaskTimelineActionSnapshot | null {
    if (!isRecord(value)) return null
    if (!isNonEmptyString(value.id, MAX_ACTION_ID_LENGTH) || !isNonEmptyString(value.name, MAX_ACTION_NAME_LENGTH)) return null
    if (!isSavedActionIcon(value.icon) || !isSavedActionColor(value.color)) return null
    if (value.templateId !== undefined && !isNonEmptyString(value.templateId, MAX_ACTION_ID_LENGTH)) return null
    return {
        id: value.id,
        name: value.name,
        icon: value.icon,
        color: value.color,
        ...(value.templateId ? { templateId: value.templateId } : {}),
    }
}

function parseTimelineEntry(value: unknown): TaskTimelineEntry | null {
    if (!isRecord(value)) return null
    if (!isNonEmptyString(value.id, MAX_ENTRY_ID_LENGTH) || !isNonEmptyString(value.createdAt, 80)) return null
    let anchor: TaskTimelineAnchor | undefined
    if (value.anchor !== undefined) {
        const parsed = parseAnchor(value.anchor)
        if (!parsed) return null
        anchor = parsed
    }

    if (value.kind === "chapter") {
        if (!isNonEmptyString(value.title, MAX_CHAPTER_TITLE_LENGTH) || (value.source !== "agent" && value.source !== "user")) return null
        return {
            id: value.id,
            kind: "chapter",
            title: value.title,
            source: value.source,
            createdAt: value.createdAt,
            ...(anchor ? { anchor } : {}),
        }
    }

    if (value.kind === "action") {
        if (!Array.isArray(value.actions) || value.actions.length === 0 || value.actions.length > 8 || typeof value.withInstructions !== "boolean") return null
        const actions = value.actions.map(parseActionSnapshot)
        if (actions.some((snapshot) => snapshot === null)) return null
        return {
            id: value.id,
            kind: "action",
            actions: actions.filter((snapshot): snapshot is TaskTimelineActionSnapshot => snapshot !== null),
            withInstructions: value.withInstructions,
            createdAt: value.createdAt,
            ...(anchor ? { anchor } : {}),
        }
    }

    if (value.kind === "do") {
        if (!isCommentCount(value.commentCount)) return null
        return {
            id: value.id,
            kind: "do",
            commentCount: value.commentCount,
            createdAt: value.createdAt,
            ...(anchor ? { anchor } : {}),
        }
    }

    return null
}

function serializedRecordSize(record: TaskTimelineRecord): number {
    return new TextEncoder().encode(JSON.stringify(record)).byteLength
}

function compareTimelineEntries(left: TaskTimelineEntry, right: TaskTimelineEntry): number {
    return left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id)
}

export function parseTaskTimelineRecord(value: unknown, expectedTaskId?: string): TaskTimelineRecord | null {
    if (!isRecord(value) || value.v !== 1 || !isNonEmptyString(value.taskId, MAX_TASK_ID_LENGTH)) return null
    if (expectedTaskId && value.taskId !== expectedTaskId) return null
    if (!Array.isArray(value.entries) || value.entries.length > MAX_TIMELINE_ENTRIES || !isNonEmptyString(value.updatedAt, 80)) return null

    const entries: TaskTimelineEntry[] = []
    const ids = new Set<string>()
    for (const candidate of value.entries) {
        const entry = parseTimelineEntry(candidate)
        if (!entry || ids.has(entry.id)) return null
        ids.add(entry.id)
        entries.push(entry)
    }
    entries.sort(compareTimelineEntries)

    const record: TaskTimelineRecord = {
        v: 1,
        taskId: value.taskId,
        entries,
        updatedAt: value.updatedAt,
    }
    return serializedRecordSize(record) <= MAX_TIMELINE_RECORD_BYTES ? record : null
}

function validateRecord(record: TaskTimelineRecord): void {
    if (!parseTaskTimelineRecord(record, record.taskId)) throw new Error("The task timeline record is invalid or too large")
}

function timelineKey(taskId: string): string {
    return `${TASK_TIMELINE_KEY_PREFIX}${taskId}${TASK_TIMELINE_KEY_SUFFIX}`
}

function taskIdFromTimelineKey(key: string): string | null {
    if (!key.startsWith(TASK_TIMELINE_KEY_PREFIX) || !key.endsWith(TASK_TIMELINE_KEY_SUFFIX)) return null
    const taskId = key.slice(TASK_TIMELINE_KEY_PREFIX.length, -TASK_TIMELINE_KEY_SUFFIX.length)
    return taskId || null
}

function emptyRecord(taskId: string, updatedAt: string): TaskTimelineRecord {
    return { v: 1, taskId, entries: [], updatedAt }
}

function actionSnapshot(savedAction: SavedAction): TaskTimelineActionSnapshot {
    return {
        id: savedAction.id,
        name: savedAction.name,
        icon: savedAction.icon,
        color: savedAction.color,
        ...(savedAction.source?.templateId ? { templateId: savedAction.source.templateId } : {}),
    }
}

export class TaskTimelineStore {
    private records = new Map<string, TaskTimelineRecord>()
    private errors = new Map<string, string>()
    private readonly versions = new Map<string, number>()
    private readonly workspaces = new Map<string, string>()
    private readonly loadingTaskIds = new Set<string>()
    private readonly pendingRefreshTaskIds = new Set<string>()
    private readonly loadPromises = new Map<string, Promise<void>>()
    private readonly writeQueues = new Map<string, Promise<void>>()
    private readonly request: TaskTimelineRequest
    private readonly client: TaskTimelineSubscriptionClient
    private readonly now: () => string
    private notificationDisposer: (() => void) | null = null
    private subscriptionDisposer: (() => void) | null = null
    private subscriptionPromise: Promise<void> | null = null

    constructor(options: TaskTimelineStoreOptions = {}) {
        this.request = options.request ?? defaultRequest
        this.client = options.client ?? nestClient
        this.now = options.now ?? (() => new Date().toISOString())
        makeObservable<TaskTimelineStore, "records" | "errors" | "clearLocalTask">(this, {
            records: observable.shallow,
            errors: observable.shallow,
            clearLocalTask: action,
        })
    }

    entriesForTask(taskId: string): readonly TaskTimelineEntry[] {
        return this.records.get(taskId)?.entries ?? []
    }

    errorForTask(taskId: string): string | null {
        return this.errors.get(taskId) ?? null
    }

    async loadTask(taskId: string, workspaceId: string): Promise<void> {
        const existingWorkspace = this.workspaces.get(taskId)
        if (this.records.has(taskId) && existingWorkspace === workspaceId) return
        const existingLoad = this.loadPromises.get(taskId)
        if (existingLoad && existingWorkspace === workspaceId) return existingLoad

        this.workspaces.set(taskId, workspaceId)
        const load = this.performLoad(taskId, workspaceId)
        this.loadPromises.set(taskId, load)
        try {
            await load
        } finally {
            if (this.loadPromises.get(taskId) === load) this.loadPromises.delete(taskId)
        }
    }

    appendChapter(params: AppendTaskTimelineChapterParams): Promise<void> {
        const createdAt = params.createdAt ?? this.now()
        const entry: TaskTimelineChapterEntry = {
            id: params.id ?? `chapter.${ulid()}`,
            kind: "chapter",
            title: params.title.trim(),
            source: params.source,
            createdAt,
            ...(params.anchor ? { anchor: { ...params.anchor } } : {}),
        }
        return this.appendEntry(params.taskId, params.workspaceId, entry)
    }

    appendAction(params: AppendTaskTimelineActionParams): Promise<void> {
        const createdAt = params.createdAt ?? this.now()
        const entry: TaskTimelineActionEntry = {
            id: params.id ?? `action.${ulid()}`,
            kind: "action",
            actions: params.actions.map(actionSnapshot),
            withInstructions: params.withInstructions,
            createdAt,
            ...(params.anchor ? { anchor: { ...params.anchor } } : {}),
        }
        return this.appendEntry(params.taskId, params.workspaceId, entry, params.replacesEntryId)
    }

    appendDo(params: AppendTaskTimelineDoParams): Promise<void> {
        const createdAt = params.createdAt ?? this.now()
        const entry: TaskTimelineDoEntry = {
            id: params.id ?? `do.${ulid()}`,
            kind: "do",
            commentCount: params.commentCount,
            createdAt,
            ...(params.anchor ? { anchor: { ...params.anchor } } : {}),
        }
        return this.appendEntry(params.taskId, params.workspaceId, entry)
    }

    removeAction(params: RemoveTaskTimelineActionParams): Promise<void> {
        return this.queueMutation(params.taskId, params.workspaceId, {
            type: "remove",
            entryId: `action.${params.turnId}`,
        })
    }

    removeDo(params: RemoveTaskTimelineDoParams): Promise<void> {
        return this.queueMutation(params.taskId, params.workspaceId, {
            type: "remove",
            entryId: `do.${params.clientRequestId}`,
        })
    }

    moveInvocation(params: MoveTaskTimelineInvocationParams): Promise<void> {
        return this.queueMutation(params.taskId, params.workspaceId, {
            type: "move",
            fromEntryId: params.fromEntryId,
            toEntryId: params.toEntryId,
            anchor: params.anchor,
        })
    }

    async deleteTask(taskId: string, workspaceId: string): Promise<void> {
        await retryBusy(() =>
            this.request("appData.delete", {
                namespace: TASK_TIMELINE_NAMESPACE,
                key: timelineKey(taskId),
                scope: { workspaceId },
            })
        )
        this.clearLocalTask(taskId)
    }

    dispose(): void {
        this.notificationDisposer?.()
        this.notificationDisposer = null
        this.subscriptionDisposer?.()
        this.subscriptionDisposer = null
        this.subscriptionPromise = null
        runInAction(() => {
            this.records.clear()
            this.errors.clear()
        })
        this.versions.clear()
        this.workspaces.clear()
        this.loadingTaskIds.clear()
        this.pendingRefreshTaskIds.clear()
        this.loadPromises.clear()
        this.writeQueues.clear()
    }

    private async performLoad(taskId: string, workspaceId: string): Promise<void> {
        await this.ensureSubscription()
        this.loadingTaskIds.add(taskId)
        try {
            await this.refresh(taskId, workspaceId)
        } finally {
            this.loadingTaskIds.delete(taskId)
        }
        if (this.pendingRefreshTaskIds.delete(taskId)) await this.refresh(taskId, workspaceId)
    }

    private async ensureSubscription(): Promise<void> {
        if (this.subscriptionDisposer) return
        if (this.subscriptionPromise) return this.subscriptionPromise
        const subscription = this.installSubscription()
        this.subscriptionPromise = subscription
        try {
            await subscription
        } finally {
            if (this.subscriptionPromise === subscription) this.subscriptionPromise = null
        }
    }

    private async installSubscription(): Promise<void> {
        this.notificationDisposer = this.client.on("appData.changed", (params) => {
            if (params?.namespace !== TASK_TIMELINE_NAMESPACE) return
            const taskId = taskIdFromTimelineKey(params.key)
            if (!taskId) return
            const workspaceId = this.workspaces.get(taskId)
            if (!workspaceId || (params.workspaceId && params.workspaceId !== workspaceId)) return
            if (this.loadingTaskIds.has(taskId)) {
                this.pendingRefreshTaskIds.add(taskId)
                return
            }
            this.queueRefresh(taskId, workspaceId)
        })
        try {
            this.subscriptionDisposer = await this.client.retainSubscriptions([
                {
                    method: "appData.changed",
                    appDataNamespace: TASK_TIMELINE_NAMESPACE,
                    appDataPrefix: TASK_TIMELINE_KEY_PREFIX,
                },
            ])
        } catch (subscriptionError) {
            this.notificationDisposer?.()
            this.notificationDisposer = null
            throw subscriptionError
        }
    }

    private queueRefresh(taskId: string, workspaceId: string): void {
        const previous = this.writeQueues.get(taskId) ?? Promise.resolve()
        const next = previous.then(
            () => this.refresh(taskId, workspaceId),
            () => this.refresh(taskId, workspaceId)
        )
        this.writeQueues.set(
            taskId,
            next.catch((error) => {
                console.warn("[TaskTimelineStore] Failed to refresh task timeline", { taskId, error })
            })
        )
    }

    private async refresh(taskId: string, workspaceId: string): Promise<void> {
        const result = await retryBusy(() =>
            this.request("appData.get", {
                namespace: TASK_TIMELINE_NAMESPACE,
                key: timelineKey(taskId),
                scope: { workspaceId },
            })
        )
        if (!result.found) {
            this.versions.delete(taskId)
            runInAction(() => {
                this.records.set(taskId, emptyRecord(taskId, this.now()))
                this.errors.delete(taskId)
            })
            return
        }

        const record = parseTaskTimelineRecord(result.value, taskId)
        if (!record || result.version === undefined) {
            runInAction(() => {
                this.records.set(taskId, emptyRecord(taskId, this.now()))
                this.errors.set(taskId, "This task timeline uses an unsupported or invalid data format. No changes were made.")
            })
            return
        }
        this.versions.set(taskId, result.version)
        runInAction(() => {
            this.records.set(taskId, record)
            this.errors.delete(taskId)
        })
    }

    private appendEntry(taskId: string, workspaceId: string, entry: TaskTimelineEntry, replacesEntryId?: string): Promise<void> {
        return this.queueMutation(taskId, workspaceId, { type: "append", entry, replacesEntryId })
    }

    private queueMutation(taskId: string, workspaceId: string, mutation: TaskTimelineMutation): Promise<void> {
        const previous = this.writeQueues.get(taskId) ?? Promise.resolve()
        const next = previous.then(
            () => this.writeMutation(taskId, workspaceId, mutation, true),
            () => this.writeMutation(taskId, workspaceId, mutation, true)
        )
        this.writeQueues.set(
            taskId,
            next.catch(() => undefined)
        )
        return next
    }

    private async writeMutation(taskId: string, workspaceId: string, mutation: TaskTimelineMutation, retryConflict: boolean): Promise<void> {
        await this.loadTask(taskId, workspaceId)
        const error = this.errors.get(taskId)
        if (error) throw new Error(error)
        const current = this.records.get(taskId) ?? emptyRecord(taskId, this.now())
        let entries: TaskTimelineEntry[]
        if (mutation.type === "move") {
            entries = current.entries.some((candidate) => candidate.id === mutation.fromEntryId)
                ? current.entries
                      .filter((candidate) => candidate.id !== mutation.toEntryId || candidate.id === mutation.fromEntryId)
                      .map((candidate) =>
                          candidate.id === mutation.fromEntryId ? { ...candidate, id: mutation.toEntryId, anchor: { ...mutation.anchor } } : candidate
                      )
                      .sort(compareTimelineEntries)
                : current.entries
        } else {
            const removedEntryId = mutation.type === "remove" ? mutation.entryId : mutation.replacesEntryId
            const retainedEntries = removedEntryId ? current.entries.filter((candidate) => candidate.id !== removedEntryId) : current.entries
            const entry = mutation.type === "append" ? mutation.entry : null
            const entryAlreadyExists = entry ? retainedEntries.some((candidate) => candidate.id === entry.id) : false
            entries = [...retainedEntries, ...(entry && !entryAlreadyExists ? [entry] : [])].sort(compareTimelineEntries)
        }
        const unchanged = entries.length === current.entries.length && entries.every((candidate, index) => candidate === current.entries[index])
        if (unchanged) return

        const record: TaskTimelineRecord = {
            v: 1,
            taskId,
            entries,
            updatedAt: this.now(),
        }
        validateRecord(record)

        try {
            const result = await retryBusy(() =>
                this.request("appData.set", {
                    namespace: TASK_TIMELINE_NAMESPACE,
                    key: timelineKey(taskId),
                    scope: { workspaceId },
                    expectedVersion: this.versions.get(taskId) ?? 0,
                    value: record,
                })
            )
            this.versions.set(taskId, result.version)
            runInAction(() => {
                this.records.set(taskId, record)
                this.errors.delete(taskId)
            })
        } catch (writeError) {
            if (!retryConflict || !(writeError instanceof NestClientError) || writeError.code !== "version_conflict") throw writeError
            await this.refresh(taskId, workspaceId)
            await this.writeMutation(taskId, workspaceId, mutation, false)
        }
    }

    private clearLocalTask(taskId: string): void {
        this.records.delete(taskId)
        this.errors.delete(taskId)
        this.versions.delete(taskId)
        this.workspaces.delete(taskId)
        this.loadingTaskIds.delete(taskId)
        this.pendingRefreshTaskIds.delete(taskId)
        this.loadPromises.delete(taskId)
        this.writeQueues.delete(taskId)
    }
}
